import React, { useCallback, useMemo } from "react";
import { CategorizedCheckboxOptions, CheckboxOption } from "../types";

type AllItemProps = {
  placeholder: string;
  handleSelectAll: () => void;
  handleDeselectAll: () => void;
  allChecked: boolean;
};

export const AllItem = ({
  placeholder,
  handleSelectAll,
  handleDeselectAll,
  allChecked,
}: AllItemProps) => {
  return (
    <CheckboxButton
      item={{
        label: placeholder,
        value: "all",
      }}
      isChecked={allChecked}
      onClick={() => {
        if (allChecked) {
          handleDeselectAll();
        } else {
          handleSelectAll();
        }
      }}
    />
  );
};

const createCategorizedOptions = (
  withCategory: boolean,
  options: CheckboxOption[] | CategorizedCheckboxOptions[],
): CategorizedCheckboxOptions[] => {
  return withCategory
    ? (options as CategorizedCheckboxOptions[])
    : [{ "": options as CheckboxOption[] }];
};

const filterSearchOptions = (
  categorizedOptions: CategorizedCheckboxOptions[],
  searchTerm: string,
): CategorizedCheckboxOptions[] => {
  return categorizedOptions?.reduce<CategorizedCheckboxOptions[]>(
    (acc, item) => {
      const categoryName = Object.keys(item)[0];
      const theseItems = item[categoryName];

      const theseItemsFiltered = theseItems.filter((option) =>
        `${option.label}`.toLowerCase().includes(searchTerm.toLowerCase()),
      );

      if (theseItemsFiltered.length > 0) {
        return [...acc, { [categoryName]: theseItemsFiltered }];
      }
      return acc;
    },
    [],
  );
};

type OptionsProps = {
  withCategory: boolean;
  options: CheckboxOption[] | CategorizedCheckboxOptions[];
  searchTerm: string;
  checkedItems: string[];
  handleCheck: (value: string) => void;
  handleUncheck: (value: string) => void;
};

const OptionsComponent = ({
  withCategory,
  options,
  searchTerm,
  checkedItems,
  handleCheck,
  handleUncheck,
}: OptionsProps) => {
  const onCheck = useCallback(
    (value: string) => {
      handleCheck(value);
    },
    [handleCheck],
  );
  const onUncheck = useCallback(
    (value: string) => {
      handleUncheck(value);
    },
    [handleUncheck],
  );
  const categorized = useMemo(
    () => createCategorizedOptions(withCategory, options),
    [withCategory, options],
  );

  const filtered = useMemo(
    () => filterSearchOptions(categorized, searchTerm),
    [categorized, searchTerm],
  );

  return (
    <>
      {filtered.map((item, index) => {
        const thisName = Object.keys(item)[0];
        const theseItems = item[thisName];
        return (
          <div key={thisName || index}>
            {thisName && (
              <h3 className="text-[#aaa] text-[12px] mb-1 mt-2">{thisName}</h3>
            )}
            {theseItems?.map((subItem: { value: string; label: string }) => {
              return (
                <CheckboxItem
                  key={subItem.value}
                  item={subItem}
                  isChecked={checkedItems?.includes(subItem.value)}
                  handleCheck={onCheck}
                  handleUncheck={onUncheck}
                />
              );
            })}
          </div>
        );
      })}
    </>
  );
};

const _CheckboxButton = ({
  item,
  isChecked,
  onClick,
}: {
  item: CheckboxOption;
  isChecked: boolean;
  onClick: () => void;
}) => {
  return (
    <div
      key={item.value}
      className="px-1 py-1.5 gap-[5px] accent-[#0C98D0] w-full group cursor-pointer hover:bg-[#F5F5F5] rounded-xs"
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        <div className="h-[16px] w-[16px]">
          <input
            type="checkbox"
            className="h-[16px] w-[16px] border rounded-[1px] cursor-pointer mt-1"
            checked={isChecked}
            onChange={() => {}}
          />
        </div>
        {/* <Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 0 }}> */}
        <div className="text-[14px] text-ellipsis">{item.label}</div>
        {/* </Paragraph> */}
      </div>
    </div>
    // </Tooltip>
  );
};
const CheckboxButton = React.memo(_CheckboxButton);

const _CheckboxItem = ({
  item,
  isChecked,
  handleCheck,
  handleUncheck,
}: {
  item: { value: string; label: string };
  isChecked: boolean;
  handleCheck: (value: string) => void;
  handleUncheck: (value: string) => void;
}) => {
  return (
    <CheckboxButton
      key={item.value}
      item={item}
      isChecked={isChecked}
      onClick={() => {
        if (isChecked) {
          handleUncheck(item.value);
        } else {
          handleCheck(item.value);
        }
      }}
    />
  );
};

const CheckboxItem = React.memo(_CheckboxItem);

export const Options = React.memo(OptionsComponent);
